import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { scroller } from "react-scroll";
import CloseIcon from "../../assets/svg/CloseIcon";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import LoginModal from "../../screens/Login";
import { doLogout } from "../../utils/auth";
import CartWidget from "../CartWidget";

const PUBLIC_SERVICES = [
  ["public-auctions", "Public Auctions"],
  ["debt-collections", "Debt Collections"],
  ["court-process-service", "Court Process Service"],
  ["repossessions-of-chattels-mortgage", "Repossessions of Chattels / Mortgage"],
  ["distress-of-rent", "Distress of Rent Service"],
  ["realisation-of-charged-securities", "Realisation of Charged Securities / Property Auctions"],
  ["private-investigations", "Private Investigations"],
  ["court-warrants-executions", "Court Warrants Executions"],
  ["other-services", "Other Services"],
];

export default function Sidebar({ sidebarOpen, toggleSidebar }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [showLogin, setShowLogin] = useState(false);

  const isHomePath = (path) => {
    return path === "/" || /^\/properties(\/|$)/.test(path) || /^\/services(\/|$)/.test(path) || path === "/about-us" || path === "/clients" || path === "/contact";
  };

  const [mode, setMode] = useState(() => (isHomePath(window.location.pathname) ? "home" : "admin"));

  const getAuthFromStorage = useCallback(() => {
    const raw = localStorage.getItem("user");
    const u = raw ? JSON.parse(raw) : null;
    const adminYn = localStorage.getItem("adminYn");
    const isAdmin = adminYn === "Y";
    return { user: u, isAdmin };
  }, []);

  const [{ user, isAdmin }, setAuth] = useState(() => getAuthFromStorage());

  useEffect(() => {
    setMode(isHomePath(location.pathname) ? "home" : "admin");
  }, [location.pathname]);

  useEffect(() => {
    const handleAuthEvent = () => setAuth(getAuthFromStorage());
    const onStorage = (e) => {
      if (["user", "adminYn", "token"].includes(e.key)) handleAuthEvent();
    };

    window.addEventListener("nava-login", handleAuthEvent);
    window.addEventListener("nava-logout", handleAuthEvent);
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("nava-login", handleAuthEvent);
      window.removeEventListener("nava-logout", handleAuthEvent);
      window.removeEventListener("storage", onStorage);
    };
  }, [getAuthFromStorage]);

  const handleNavTo = (section) => {
    toggleSidebar(false);

    if (section === "properties") {
      navigate("/properties");
      return;
    }

    if (location.pathname === "/") {
      scroller.scrollTo(section, {
        duration: 600,
        smooth: true,
        offset: -80,
      });
    } else {
      navigate("/", { state: { scrollTo: section } });
    }
  };

  const goHomeMode = () => {
    toggleSidebar(false);
    navigate("/");
  };

  const goToDashboard = () => {
    toggleSidebar(false);
    navigate("/admin");
  };

  const goToAdminLogin = () => {
    toggleSidebar(false);
    navigate("/admin/login");
  };

  const handleLogout = () => {
    doLogout(navigate);
    setAuth({ user: null, isAdmin: false });
    toggleSidebar(false);
    setMode("home");
  };

  return (
    <Wrapper className="animate darkBg" sidebarOpen={sidebarOpen}>
      <ScrollArea>
        <SidebarHeader className="flexSpaceCenter">
          <LoginModal open={showLogin} onClose={() => setShowLogin(false)} />
          <div className="flexNullCenter">
            <div style={{ textAlign: "center" }}>
              <img
                src="/sare-logo.png"
                alt=" Sare Auctioneers logo"
                style={{
                  width: 80,
                  height: 50,
                  objectFit: "contain",
                  display: "block",
                  margin: "0 auto",
                }}
                loading="lazy"
              />
            </div>
            <div style={{ textAlign: "center" }}>
              <h1 style={{ fontSize: "20px", fontWeight: "800", margin: 0, color: "#5d191a" }}>SARE</h1>
              <h2 style={{ fontSize: "14px", fontWeight: "800", margin: 0, color: "#8a3638" }}>AUCTIONEERS</h2>
            </div>
          </div>

          <CloseBtn onClick={() => toggleSidebar(!sidebarOpen)} className="animate pointer" aria-label="Close sidebar">
            <CloseIcon />
          </CloseBtn>
        </SidebarHeader>

        <NavList role="menu">
          <NavLi role="none">
            <NavButton role="menuitem" onClick={() => handleNavTo("home")} className="whiteColor">
              Home
            </NavButton>
          </NavLi>
          <NavLi role="none">
            <NavButton role="menuitem" onClick={() => navigate("/services")} className="whiteColor">
              Services
            </NavButton>
            <MobileServiceList>
              {PUBLIC_SERVICES.map(([slug, label]) => (
                <SubNavButton
                  key={slug}
                  role="menuitem"
                  onClick={() => { toggleSidebar(false); navigate(`/services/${slug}`); }}
                  className="whiteColor"
                >
                  {label}
                </SubNavButton>
              ))}
            </MobileServiceList>
          </NavLi>
          <NavLi role="none">
            <NavButton role="menuitem" onClick={() => handleNavTo("properties")} className="whiteColor">
              Properties
            </NavButton>
          </NavLi>
          <NavLi role="none">
            <NavButton role="menuitem" onClick={() => { toggleSidebar(false); navigate("/about-us"); }} className="whiteColor">
              About Us
            </NavButton>
          </NavLi>
          <NavLi role="none">
            <NavButton role="menuitem" onClick={() => { toggleSidebar(false); navigate("/clients"); }} className="whiteColor">
              Our Clients
            </NavButton>
          </NavLi>
          <NavLi role="none">
            <NavButton role="menuitem" onClick={() => { toggleSidebar(false); navigate("/contact"); }} className="whiteColor">
              Contact
            </NavButton>
          </NavLi>

          {isAdmin && (
            <NavLi role="none">
              <NavButton role="menuitem" onClick={goToDashboard} className="whiteColor">
                Admin Dashboard
              </NavButton>
            </NavLi>
          )}
        </NavList>

        <BottomBar>
          {isAdmin ? (
            <button onClick={handleLogout} className="buttonLink whiteColor">
              Log Out
            </button>
          ) : (
            <button onClick={goToAdminLogin} className="buttonLink whiteColor">
              Admin Log In
            </button>
          )}
        </BottomBar>
        <CartWidget />
      </ScrollArea>
    </Wrapper>
  );
}

const Wrapper = styled.nav`
  width: 400px;
  height: 100vh;
  position: fixed;
  top: 0;
  right: ${(props) => (props.sidebarOpen ? "0px" : "-400px")};
  z-index: 9999;
  overflow: hidden;

  @media (max-width: 400px) {
    width: calc(100% - 56px);
  }
`;

const ScrollArea = styled.div`
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 30px 30px;
  box-sizing: border-box;
`;

const MobileServiceList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-bottom: 4px;
`;

const SidebarHeader = styled.div`
  padding: 20px 0;
`;
const CloseBtn = styled.button`
  border: 0px;
  outline: none;
  background-color: transparent;
  padding: 10px;
`;

const NavList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 10px 0 0;
  margin: 0;
  list-style: none;
`;

const NavLi = styled.li`
  margin: 12px 0;
  width: 100%;
`;

const NavButton = styled.button`
  display: block;
  width: 100%;
  padding: 10px 15px;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-weight: 600;
  font-size: 15px;
`;

const SubNavButton = styled.button`
  display: block;
  width: 100%;
  padding: 6px 15px 6px 30px;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
`;

const BottomBar = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
`;